import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { toast } from './Toast';

/**
 * Phase 3: Edit Recurrent Service Pattern Modal
 * Edits the pattern via PATCH /recurring/:id. Changes apply to future cron
 * generations only. Already-generated WOs untouched.
 * Includes non-blocking availability check for the first occurrence in next
 * calendar month (debounced 300ms).
 */
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EditRecurrencePatternModal = ({ isOpen, onClose, onSuccess, recurringService }) => {
  const [form, setForm] = useState({
    service_type: 'routine', priority: 'normal', technician_id: null,
    preferred_time_start: '09:00', estimated_duration: 60,
    recurrence_pattern: 'weekly', recurrence_config: {},
    notes: '', fifth_week_vip: false, end_date: '',
  });
  const [technicians, setTechnicians] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !recurringService) return;
    setForm({
      service_type: recurringService.service_type || 'routine',
      priority: recurringService.priority || 'normal',
      technician_id: recurringService.technician_id || null,
      preferred_time_start: recurringService.preferred_time_start
        ? String(recurringService.preferred_time_start).slice(0, 5) : '09:00',
      estimated_duration: recurringService.estimated_duration || 60,
      recurrence_pattern: recurringService.recurrence_pattern || 'weekly',
      recurrence_config: recurringService.recurrence_config || {},
      notes: recurringService.notes || '',
      fifth_week_vip: recurringService.fifth_week_vip === true,
      end_date: recurringService.end_date
        ? String(recurringService.end_date).slice(0, 10) : '',
    });
    api.get('/users').then(r => {
      const users = r.data?.data || r.data || [];
      setTechnicians(users.filter(u => u.is_technician && u.is_active));
    }).catch(e => { console.error('fetch techs:', e); setTechnicians([]); });
  }, [isOpen, recurringService]);

  if (!isOpen || !recurringService) return null;

  const handlePatternChange = (newPattern) => {
    let dc = {};
    if (newPattern === 'weekly') dc = { day_of_week: 2 };
    else if (newPattern === 'biweekly') dc = { day_of_week: 2, weeks: [1, 3] };
    else if (newPattern === 'monthly') dc = { week_of_month: 1, day_of_week: 2 };
    else if (['daily', '2x_week', '3x_week'].includes(newPattern)) dc = { days_of_week: [1, 2, 3, 4, 5] };
    else dc = { day_of_month: 1 };
    setForm(f => ({ ...f, recurrence_pattern: newPattern, recurrence_config: dc }));
  };

  const updateConfig = (key, value) => {
    setForm(f => ({ ...f, recurrence_config: { ...f.recurrence_config, [key]: value } }));
  };

  const toggleDayOfWeek = (dayIdx, checked) => {
    const days = new Set(form.recurrence_config.days_of_week || []);
    if (checked) days.add(dayIdx); else days.delete(dayIdx);
    updateConfig('days_of_week', Array.from(days).sort((a, b) => a - b));
  };

  const handleSubmit = async () => {
    if (!form.service_type) { toast.error('Service type required'); return; }
    if (!form.preferred_time_start) { toast.error('Preferred time required'); return; }
    if (!form.recurrence_pattern) { toast.error('Recurrence pattern required'); return; }
    if (!form.estimated_duration || form.estimated_duration <= 0) { toast.error('Duration must be positive'); return; }
    setSubmitting(true);
    const now = new Date();
    const local_date = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
    const local_time = String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0') + ':' + String(now.getSeconds()).padStart(2, '0');
    const payload = {
      service_type: form.service_type, priority: form.priority,
      technician_id: form.technician_id || null,
      preferred_time_start: form.preferred_time_start,
      estimated_duration: parseInt(form.estimated_duration, 10) || 60,
      // recurrence_pattern: LOCKED — not sent
      recurrence_config: form.recurrence_config,
      notes: form.notes,
      // fifth_week_vip: LOCKED — not sent
      end_date: form.end_date || null,
      local_date, local_time,
    };
    try {
      const res = await api.patch(`/recurring/${recurringService.id}`, payload);
      toast.success(res.data?.message || 'Pattern updated. Future generations will use new values.');
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.message || err.message));
    } finally { setSubmitting(false); }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4, textTransform: 'uppercase' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 8, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: 20, borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' }}>
          <h3 style={{ margin: 0, color: '#2D5C3F', fontSize: 16, fontWeight: 700 }}>✏️ Edit Recurrent Service Pattern</h3>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
            {recurringService.client_name} · {recurringService.facility_name}
          </div>
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Service Type</label>
              <select value={form.service_type} onChange={e => setForm({ ...form, service_type: e.target.value })} style={inputStyle}>
                <option value="routine">Routine</option>
                <option value="corrective">Corrective</option>
                <option value="emergency">Emergency</option>
                <option value="inspection">Inspection</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={inputStyle}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Technician</label>
            <select value={form.technician_id || ''} onChange={e => setForm({ ...form, technician_id: e.target.value ? parseInt(e.target.value, 10) : null })} style={inputStyle}>
              <option value="">— Unassigned —</option>
              {technicians.map(t => (
                <option key={t.id} value={t.id}>
                  {t.first_name} {t.last_name}{t.role && t.role !== 'technician' ? ` · ${t.role}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Preferred Time</label>
              <input type="time" value={form.preferred_time_start} onChange={e => setForm({ ...form, preferred_time_start: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Duration (minutes)</label>
              <input type="number" min="15" step="15" value={form.estimated_duration} onChange={e => setForm({ ...form, estimated_duration: e.target.value })} style={inputStyle} />
            </div>
          </div>

          <div style={{ background: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: 6, padding: 10, marginBottom: 12, fontSize: 12, color: '#374151' }}>
            <strong>🔒 Locked fields below:</strong> Frequency, weekday set (daily/2x/3x), and VIP rule are tied to the contract price. To change them, Stop this pattern and create a new one.
          </div>
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 6, padding: 12, marginBottom: 16 }}>
            <div style={{ ...labelStyle, color: '#065F46', marginBottom: 8 }}>🔁 Recurrence Pattern</div>
            <select value={form.recurrence_pattern} disabled title="Locked — frequency affects contract price" style={{ ...inputStyle, marginBottom: 12, opacity: 0.7, cursor: 'not-allowed', backgroundColor: '#F3F4F6' }}>
              <option value="daily">Daily</option>
              <option value="2x_week">2× per Week</option>
              <option value="3x_week">3× per Week</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
              <option value="bimonthly">Bi-monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="semiannual">Semi-annual</option>
              <option value="annual">Annual</option>
            </select>

            {form.recurrence_pattern === 'weekly' && (
              <div>
                <label style={labelStyle}>Day of Week</label>
                <select value={form.recurrence_config.day_of_week ?? 2} onChange={e => updateConfig('day_of_week', parseInt(e.target.value, 10))} style={inputStyle}>
                  {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
            )}

            {form.recurrence_pattern === 'biweekly' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Day of Week</label>
                  <select value={form.recurrence_config.day_of_week ?? 2} onChange={e => updateConfig('day_of_week', parseInt(e.target.value, 10))} style={inputStyle}>
                    {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Weeks</label>
                  <select value={JSON.stringify(form.recurrence_config.weeks || [1, 3])} onChange={e => updateConfig('weeks', JSON.parse(e.target.value))} style={inputStyle}>
                    <option value="[1,3]">Weeks 1 & 3</option>
                    <option value="[2,4]">Weeks 2 & 4</option>
                  </select>
                </div>
              </div>
            )}

            {form.recurrence_pattern === 'monthly' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Week of Month</label>
                  <select value={form.recurrence_config.week_of_month ?? 1} onChange={e => updateConfig('week_of_month', parseInt(e.target.value, 10))} style={inputStyle}>
                    <option value="1">First</option>
                    <option value="2">Second</option>
                    <option value="3">Third</option>
                    <option value="4">Fourth</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Day of Week</label>
                  <select value={form.recurrence_config.day_of_week ?? 2} onChange={e => updateConfig('day_of_week', parseInt(e.target.value, 10))} style={inputStyle}>
                    {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
              </div>
            )}

            {['daily', '2x_week', '3x_week'].includes(form.recurrence_pattern) && (
              <div>
                <label style={labelStyle}>Days of Week</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {DAY_NAMES.map((day, idx) => (
                    <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: 'white', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={(form.recurrence_config.days_of_week || []).includes(idx)} disabled title="Locked — days_of_week affects visit count" />
                      {day}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {['bimonthly', 'quarterly', 'semiannual', 'annual'].includes(form.recurrence_pattern) && (
              <div>
                <label style={labelStyle}>Day of Month</label>
                <input type="number" min="1" max="31" value={form.recurrence_config.day_of_month ?? 1} onChange={e => updateConfig('day_of_month', parseInt(e.target.value, 10) || 1)} style={inputStyle} />
              </div>
            )}

            {['daily', '2x_week', '3x_week', 'weekly'].includes(form.recurrence_pattern) && (
              <div style={{ marginTop: 12, padding: 8, background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#92400E', fontWeight: 600, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.fifth_week_vip} disabled title="Locked — VIP affects 5th visit pricing" />
                  ⭐ Fifth Week VIP — 5th occurrence is free for the customer
                </label>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>End Date (optional)</label>
            <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} style={inputStyle} />
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
              Leave empty for ongoing contract. To stop an existing contract use the Stop button on the row.
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Internal notes about this pattern..." />
          </div>

          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, padding: 10, fontSize: 12, color: '#1E40AF' }}>
            <strong>ℹ️ Note:</strong> Changes apply to future cron generations only. Already-generated work orders remain unchanged — edit them individually if needed.
          </div>
        </div>

        <div style={{ padding: 16, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#F9FAFB' }}>
          <button type="button" onClick={onClose} disabled={submitting} style={{ padding: '8px 16px', background: 'white', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting} style={{ padding: '8px 20px', background: submitting ? '#9CA3AF' : '#2D5C3F', color: 'white', border: 'none', borderRadius: 6, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
            {submitting ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditRecurrencePatternModal;
